import type { EventHandler, KeyboardEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGrantsUtils } from '../../../utils/utils'

import type { TaskTemplate } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import { useCandidateDetailsQueryParams } from '@percent/admin-dashboard/app/grants/programs/grantsProgramDetails/hooks/useCandidateDetailsQueryParams'
import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { capitalizeString } from '@percent/admin-dashboard/common/utility/capitalizeString'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  Badge,
  Button,
  Flex,
  Icon,
  Loader,
  Menu,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  type TableColumn,
  TableProvider,
  TableRow
} from '@percent/lemonade'
import { useQuery } from '@percent/utility'
import { AddTaskTemplateModal } from './addTaskTemplateModal/AddTaskTemplateModal'
import { DeleteTaskTemplateModal } from './deleteTaskTemplateModal/DeleteTaskTemplateModal'
import { EditTaskTemplateModal } from './editTaskTemplateModal/EditTaskTemplateModal'

type TemplateAction = 'add' | 'edit' | 'delete'

export function ProgramTaskTemplates() {
  const [activeTemplate, setActiveTemplate] = useState<{
    template?: TaskTemplate
    action: TemplateAction
  } | null>(null)

  const [templateName, setTemplateName] = useState('')

  const { formatDate } = useGrantsUtils()
  const { id: programId } = useRequiredParams()
  const { grantProgramService } = useServices()
  const { t } = useTranslation()
  const { setQueryParams, resetQueryParams } = useCandidateDetailsQueryParams()

  const {
    data: taskTemplates,
    isLoading,
    refetch: refreshTaskTemplates
  } = useQuery(['task-templates', 'program', programId], grantProgramService.getTaskTemplates, { programId })

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(event => {
    if (event.key === 'Enter') {
      const { value } = event.target as HTMLInputElement
      setTemplateName(value)
    }
  }, [])

  const handlePreviewForm = (taskTemplate: TaskTemplate) => {
    if (taskTemplate.details && 'formId' in taskTemplate.details) {
      setQueryParams({
        previewTask: taskTemplate.details.formId,
        taskTemplateName: taskTemplate?.name
      })
    }
  }

  const handlePreviewFormFromModal = (formId: string) => {
    const params = new URLSearchParams()
    params.set('previewTask', formId)

    const url = `${window.location.pathname}?${params.toString()}`
    window.open(url, '_blank')
  }

  const closeAllModals = useCallback(() => {
    setActiveTemplate(null)
    resetQueryParams()
  }, [resetQueryParams])

  const getMenuSections = (taskTemplate: TaskTemplate) => {
    const items = [
      {
        key: 'edit',
        label: t('grants.programDetails.taskTemplates.edit'),
        onClick: () => setActiveTemplate({ template: taskTemplate, action: 'edit' }),
        'data-testid': 'menu-item-edit'
      },
      {
        key: 'delete',
        label: t('grants.programDetails.taskTemplates.delete'),
        onClick: () => setActiveTemplate({ template: taskTemplate, action: 'delete' }),
        'data-testid': 'menu-item-delete'
      },
      {
        key: 'preview',
        label: t('grants.programDetails.taskTemplates.previewForm'),
        onClick: () => handlePreviewForm(taskTemplate),
        'data-testid': 'menu-item-preview'
      }
    ]

    return [{ items }]
  }

  if (isLoading) return <Loader />

  const columns: TableColumn[] = [
    {
      id: 'name',
      content: t('grants.programDetails.taskTemplates.name'),
      isSortable: false,
      props: { width: '20%' }
    },
    {
      id: 'description',
      content: t('grants.programDetails.taskTemplates.description'),
      isSortable: false,
      props: { width: '20%' }
    },
    {
      id: 'audience',
      content: t('grants.programDetails.taskTemplates.audience'),
      isSortable: false,
      props: { width: '10%' }
    },
    {
      id: 'type',
      content: t('grants.programDetails.taskTemplates.type'),
      isSortable: false,
      props: { width: '10%' }
    },
    {
      id: 'dueByDate',
      content: t('grants.programDetails.taskTemplates.dueByDate'),
      isSortable: false,
      props: { width: '10%' }
    },
    {
      id: 'hoursUntilDue',
      content: t('grants.programDetails.taskTemplates.hoursUntilDue'),
      isSortable: false,
      props: { width: '10%' }
    },
    { id: '', content: '', isSortable: false, props: { width: '2%' } }
  ]

  return (
    <TableProvider
      columns={columns}
      emptyTableTitle={t('grants.programDetails.taskTemplates.empty')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={taskTemplates?.length ?? 0}
    >
      <Flex direction="row" justify="justify-between" align="center" pb={12}>
        <Flex w="100%" maw="480" mr={8}>
          <SearchInput
            name="templateName"
            value={templateName}
            handleClearValue={() => setTemplateName('')}
            onKeyDown={handleKeyPress}
            onChange={e => setTemplateName(e.target.value)}
            placeholder={t('grants.programDetails.taskTemplates.searchPlaceholder')}
            status="default"
            inputSize="small"
          />
        </Flex>
        <Flex ml="auto">
          <Button size="small" onClick={() => setActiveTemplate({ action: 'add' })}>
            {t('grants.programDetails.taskTemplates.createTemplate')}
          </Button>
        </Flex>
      </Flex>
      <Table>
        <TableBody>
          {taskTemplates
            ?.filter(taskTemplate => taskTemplate.name.toLowerCase().includes(templateName.toLowerCase()))
            .map(taskTemplate => (
              <TableRow key={taskTemplate.id}>
                <TableCell>{taskTemplate.name}</TableCell>
                <TableCell>{taskTemplate.description}</TableCell>
                <TableCell>
                  <Badge variant="informative">{capitalizeString(taskTemplate.audience ?? '')}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="default">{taskTemplate.details?.type}</Badge>
                </TableCell>
                <TableCell>{taskTemplate.dueByDate ? formatDate(taskTemplate.dueByDate) : '-'}</TableCell>
                <TableCell>{taskTemplate.hoursUntilDue ? taskTemplate.hoursUntilDue : '-'}</TableCell>
                <TableCell>
                  <Menu
                    title={<Icon name="menu-vertical" size={4} color="gray300" data-testid="menu-vertical-icon" />}
                    sections={getMenuSections(taskTemplate)}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      {activeTemplate?.action === 'add' && (
        <AddTaskTemplateModal
          programId={programId}
          isOpen={activeTemplate?.action === 'add'}
          onClose={closeAllModals}
          refresh={refreshTaskTemplates}
          onPreviewForm={handlePreviewFormFromModal}
        />
      )}
      {activeTemplate?.template && (
        <EditTaskTemplateModal
          taskTemplate={activeTemplate.template}
          programId={programId}
          isOpen={activeTemplate?.action === 'edit'}
          onClose={closeAllModals}
          refresh={refreshTaskTemplates}
          onPreviewForm={handlePreviewFormFromModal}
        />
      )}
      {activeTemplate?.action === 'delete' && activeTemplate.template && (
        <DeleteTaskTemplateModal
          isOpen
          onClose={closeAllModals}
          programId={programId}
          taskTemplateId={activeTemplate.template.id}
          taskTemplateName={activeTemplate.template.name}
          refresh={refreshTaskTemplates}
        />
      )}
    </TableProvider>
  )
}
